import { useEffect } from "react";

import isCurrentlyOnBrandTVDomain from "@/utils/is-currently-on-btv-domain";

const MANAGED_FAVICON_CLASS = 'managed-favicon';

/**
 * Appends the domain-appropriate <link rel="icon"> tags to <head> directly.
 * Replaces the old react-helmet <Favicons> component for the custom-JS document head.
 * Use static host VITE_PUBLIC_URL/PUBLIC_URL.
 */
export default function useFavicons(customFavicon?: string): void {
	const isBrandTV = isCurrentlyOnBrandTVDomain();

	useEffect(() => {
		// A custom favicon is already set in the html by events-app-server-3, so leave it alone.
		if (customFavicon) return;

		const publicUrl = import.meta.env.VITE_PUBLIC_URL || import.meta.env.PUBLIC_URL || '';

		// Light favicon on dark mode, dark favicon on light mode.
		const favicons: { href: string; media?: string }[] = isBrandTV
			? [
				{ href: `${publicUrl}/brandtv-light-favicon.png`, media: '(prefers-color-scheme: dark)' },
				{ href: `${publicUrl}/brandtv-dark-favicon.png`, media: '(prefers-color-scheme: light)' },
			]
			: [{ href: `${publicUrl}/brandlive-favicon.png` }];

		document.head.querySelectorAll(`link.${MANAGED_FAVICON_CLASS}`).forEach(el => el.remove());
		document.head.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]').forEach(el => el.remove());

		const created = favicons.map(({ href, media }) => {
			const link = document.createElement('link');
			link.rel = 'icon';
			link.href = href;
			if (media) link.media = media;
			link.classList.add(MANAGED_FAVICON_CLASS);
			document.head.appendChild(link);
			return link;
		});

		return () => {
			created.forEach(link => link.remove());
		};
	}, [customFavicon, isBrandTV]);
}
